
import React from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

export const TermsPage: React.FC = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-12">
          <header className="mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
            <p className="text-lg text-gray-600">Last updated: December 19, 2024</p>
          </header>

          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 mb-4">
                By accessing and using the services provided by Hugh Muir Investment Corp ("Company," "we," "us," or "our"), 
                you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide 
                by the above, please do not use this service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Investment Advisory Services</h2>
              <p className="text-gray-700 mb-4">
                Hugh Muir Investment Corp provides investment advisory services, financial planning, and wealth management 
                solutions. Our services are designed to help clients achieve their financial goals through professional 
                investment strategies and personalized financial advice.
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Investment management and portfolio advisory services</li>
                <li>Financial planning and retirement planning</li>
                <li>Wealth preservation and growth strategies</li>
                <li>Risk assessment and management</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Client Responsibilities</h2>
              <p className="text-gray-700 mb-4">
                As a client of Hugh Muir Investment Corp, you agree to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Provide accurate and complete financial information</li>
                <li>Communicate changes in financial circumstances promptly</li>
                <li>Review all investment recommendations carefully</li>
                <li>Understand that all investments carry inherent risks</li>
                <li>Make final investment decisions based on your own judgment</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Investment Risks and Disclaimers</h2>
              <p className="text-gray-700 mb-4">
                <strong>Important:</strong> All investments involve risk, including the potential loss of principal. 
                Past performance does not guarantee future results. The value of investments and the income from them 
                may fluctuate and you may not get back the amount originally invested.
              </p>
              <p className="text-gray-700 mb-4">
                Hugh Muir Investment Corp does not guarantee any specific investment outcomes or returns. Market conditions, 
                economic factors, and other variables beyond our control may affect investment performance.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Fees and Compensation</h2>
              <p className="text-gray-700 mb-4">
                Our fee structure is transparent and will be clearly disclosed in your investment advisory agreement. 
                Fees may include:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Asset-based management fees</li>
                <li>Financial planning fees</li>
                <li>Performance-based compensation (where applicable)</li>
                <li>Third-party custodial and transaction fees</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Confidentiality and Privacy</h2>
              <p className="text-gray-700 mb-4">
                We are committed to protecting your privacy and maintaining the confidentiality of your personal and 
                financial information. We will not disclose your information to third parties without your consent, 
                except as required by law or regulation.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Regulatory Compliance</h2>
              <p className="text-gray-700 mb-4">
                Hugh Muir Investment Corp operates in compliance with all applicable federal and state securities laws 
                and regulations. We are registered as an investment adviser and adhere to fiduciary standards in our 
                client relationships.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">
                To the fullest extent permitted by law, Hugh Muir Investment Corp shall not be liable for any indirect, 
                incidental, special, consequential, or punitive damages, including but not limited to loss of profits, 
                data, or use, arising out of or in connection with our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Termination</h2>
              <p className="text-gray-700 mb-4">
                Either party may terminate the advisory relationship at any time with written notice. Upon termination, 
                you will be responsible for fees incurred up to the termination date, and we will assist with the 
                orderly transfer of your accounts.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Governing Law</h2>
              <p className="text-gray-700 mb-4">
                These terms shall be governed by and construed in accordance with the laws of the State of Illinois, 
                without regard to its conflict of law provisions. Any disputes arising under these terms shall be 
                subject to the exclusive jurisdiction of the courts of Illinois.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Changes to Terms</h2>
              <p className="text-gray-700 mb-4">
                We reserve the right to modify these terms at any time. Changes will be effective immediately upon 
                posting on our website. Your continued use of our services after any changes constitutes acceptance 
                of the new terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-2"><strong>Hugh Muir Investment Corp</strong></p>
                <p className="text-gray-700 mb-2">1 Tiffany Point Suite G 10</p>
                <p className="text-gray-700 mb-2">Bloomington, IL 60618</p>
                <p className="text-gray-700 mb-2">Phone: (773) 850-0803</p>
                <p className="text-gray-700">Email: info@hughmuir.com</p>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TermsPage;
